"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import { useSession } from "next-auth/react";
import {
  Features,
  FeatureAccessLevel,
  AccessLevel,
} from "@/prisma-package/src";
import { getUserRolesByEmailAction } from "../actions/user-roles.actions";
import {
  FeaturesEnum,
  FeatureAccessLevelEnum,
} from "@/app/_lib/types/feature-permissions.types";
import { useOrg } from "@/app/_lib/contexts/org-context";

type FeaturePermissionsMap = {
  [key in Features]: FeatureAccessLevel;
};

type RoleType = {
  id: string;
  userId: string;
  roleId: string;
  role: {
    id: string;
    name: string;
    orgId: string;
    accessLevel: AccessLevel;
    featurePermissions: any; // This is the PrismaJson.FeaturePermissions
    isShared: boolean;
    projectId: string | null;
  };
};

type FeaturePermissionsResult = {
  featurePermissions: FeaturePermissionsMap;
  isLoading: boolean;
  error: Error | null;
};

const DEFAULT_PERMISSIONS: FeaturePermissionsMap = {
  [FeaturesEnum.INCENTIVE_INDEX]: FeatureAccessLevelEnum.NONE,
  [FeaturesEnum.RULE_ENGINE]: FeatureAccessLevelEnum.NONE,
  [FeaturesEnum.ESTIMATOR]: FeatureAccessLevelEnum.NONE,
  [FeaturesEnum.PROJECT_DASHBOARD]: FeatureAccessLevelEnum.NONE,
  [FeaturesEnum.AUDIT]: FeatureAccessLevelEnum.NONE,
};

/**
 * A hook that checks a user's roles for each individual feature.
 * It automatically fetches the user's email and organization from Clerk,
 * and their roles from the database.
 *
 * @returns Object with feature permissions and helper methods
 */
export const useFeaturePermissions = (
  projectId?: string | null,
  options?: {
    userId?: string | null;
    orgId?: string | null;
    userRoles?: RoleType[];
  }
): FeaturePermissionsResult => {
  // Using options allows us to inject values for testing
  const testMode = !!options;

  const { data: session, status } = useSession();
  const { orgId: urlOrgId } = useOrg();

  const isUserLoaded = status !== "loading";
  const isOrgLoaded = status !== "loading";
  const user = !testMode && session?.user ? session.user : null;
  const organization = !testMode && urlOrgId ? { id: urlOrgId } : null;

  const [isLoading, setIsLoading] = useState(!testMode);
  const [error, setError] = useState<Error | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [orgId, setOrgId] = useState<string | null>(options?.orgId || null);
  const [userRoles, setUserRoles] = useState<RoleType[]>(
    options?.userRoles || []
  );
  const [hasFetchedRoles, setHasFetchedRoles] = useState(testMode);
  const [permissions, setPermissions] =
    useState<FeaturePermissionsMap>(DEFAULT_PERMISSIONS);
  const [retryCount, setRetryCount] = useState(0);
  const maxRetries = 3;
  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Fetch email and orgId from session
  useEffect(() => {
    // Skip if in test mode or if session hasn't loaded yet
    if (testMode || !isUserLoaded || !isOrgLoaded) return;

    if (user) {
      const primaryEmail = user.email;
      if (primaryEmail) {
        setEmail(primaryEmail);
      }
    }

    if (organization) {
      setOrgId(organization.id);
    }
  }, [user, organization, isUserLoaded, isOrgLoaded, testMode]);

  // Fetch user roles from database
  const fetchUserRoles = useCallback(async () => {
    if (testMode || !email || !orgId) return;

    try {
      const [err, res] = await getUserRolesByEmailAction({
        email,
      });

      if (err || !res || !res.roles) {
        throw new Error("Failed to get user roles by email");
      }

      setUserRoles(res.roles || []);
      setHasFetchedRoles(true);
      setError(null);
    } catch (err) {
      console.error("Error fetching user roles:", err);
      setError(err instanceof Error ? err : new Error(String(err)));
      setHasFetchedRoles(true);
    }
  }, [email, orgId, testMode]);

  useEffect(() => {
    fetchUserRoles();
  }, [fetchUserRoles]);

  // Retry mechanism for failed role fetching
  useEffect(() => {
    if (error && retryCount < maxRetries) {
      retryTimeoutRef.current = setTimeout(() => {
        setRetryCount((prev) => prev + 1);
        setError(null);
        setHasFetchedRoles(false);
        fetchUserRoles();
      }, 1000 * Math.pow(2, retryCount));

      return () => {
        if (retryTimeoutRef.current) {
          clearTimeout(retryTimeoutRef.current);
        }
      };
    }
  }, [error, retryCount, maxRetries, fetchUserRoles]);

  // Calculate permissions based on roles and projectId
  useEffect(() => {
    // In test mode, we still need to calculate permissions based on the provided roles
    // Don't prematurely set isLoading=false before we have the data we need
    if (!testMode && (!email || !orgId || !hasFetchedRoles)) {
      return;
    }

    try {
      // Check if user belongs to admin organization
      if (orgId === process.env.NEXT_PUBLIC_ADMIN_ORG_ID) {
        // Grant edit access to all features for admin organization users
        const adminPermissions: FeaturePermissionsMap = {
          [FeaturesEnum.INCENTIVE_INDEX]: FeatureAccessLevelEnum.EDIT,
          [FeaturesEnum.RULE_ENGINE]: FeatureAccessLevelEnum.EDIT,
          [FeaturesEnum.ESTIMATOR]: FeatureAccessLevelEnum.EDIT,
          [FeaturesEnum.PROJECT_DASHBOARD]: FeatureAccessLevelEnum.EDIT,
          [FeaturesEnum.AUDIT]: FeatureAccessLevelEnum.EDIT,
        };
        setPermissions(adminPermissions);
        setIsLoading(false);
        return;
      }
      
      // If no roles, return default permissions
      if (userRoles.length === 0) {
        setPermissions(DEFAULT_PERMISSIONS);
        setIsLoading(false);
        return;
      }

      // Get all organization-wide roles for the current organization
      const orgRoles = userRoles.filter(
        (userRole) =>
          userRole.role.orgId === orgId &&
          userRole.role.accessLevel === AccessLevel.COMPANY
      );

      // If there are multiple COMPANY roles, this is an error condition
      if (orgRoles.length > 1) {
        throw new Error(
          "Invalid role configuration: Multiple COMPANY roles found for organization"
        );
      }

      // If we have a projectId, check for project-specific roles
      if (projectId) {
        const getProjectRoles = (projectId: string): RoleType[] => {
          return userRoles.filter(
            (userRole) =>
              userRole.role.projectId === projectId &&
              userRole.role.accessLevel === AccessLevel.PROJECT
          );
        };
        const projectRoles = getProjectRoles(projectId);

        // If there's exactly one project role, use its permissions
        if (projectRoles.length === 1) {
          const rolePermissions = projectRoles[0].role
            .featurePermissions as FeaturePermissionsMap;
          setPermissions(rolePermissions || DEFAULT_PERMISSIONS);
          setIsLoading(false);
          return;
        }

        // If there are multiple project roles, throw an error
        if (projectRoles.length > 1) {
          throw new Error(
            `Invalid role configuration: Multiple PROJECT roles found for project ${projectId}`
          );
        }
      }

      // If it is a COMPANY role, set permissions to that role
      const companyRole = orgRoles[0];
      if (companyRole) {
        const rolePermissions = companyRole.role
          .featurePermissions as FeaturePermissionsMap;
        setPermissions(rolePermissions || DEFAULT_PERMISSIONS);
        setIsLoading(false);
        return;
      }

      // At this point, we know there are no COMPANY roles and no PROJECT roles for the current project
      // Check if there are PROJECT roles for the current organization
      const projectRoles = userRoles.filter(
        (userRole) =>
          userRole.role.orgId === orgId &&
          userRole.role.accessLevel === AccessLevel.PROJECT
      );

      // If there are no PROJECT roles either, use default permissions
      if (projectRoles.length === 0) {
        setPermissions(DEFAULT_PERMISSIONS);
        setIsLoading(false);
        return;
      }

      // We have PROJECT roles but none for the current project - use default permissions
      setPermissions(DEFAULT_PERMISSIONS);
    } catch (err) {
      console.error("Error calculating feature permissions:", err);
      setError(err instanceof Error ? err : new Error(String(err)));
      setPermissions(DEFAULT_PERMISSIONS);
    } finally {
      setIsLoading(false);
    }
  }, [email, orgId, userRoles, testMode, projectId, hasFetchedRoles]);

  return {
    featurePermissions: permissions,
    isLoading,
    error,
  };
};
